import { useEffect, useRef } from "react";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import {
  ClassicEditor,
  Bold,
  Italic,
  Underline,
  Strikethrough,
  FontSize,
  FontColor,
  FontBackgroundColor,
  Heading,
  BlockQuote,
  Link,
  List,
  ListProperties,
  Alignment,
  Table,
  TableToolbar,
  TableCellProperties,
  TableProperties,
  Image,
  ImageInsertViaUrl,
  ImageUpload,
  ImageInsert,
  ImageResize,
  ImageStyle,
  ImageToolbar,
  MediaEmbed,
  HorizontalLine,
  Indent,
  IndentBlock,
  Undo,
  Essentials,
  Paragraph,
  CodeBlock,
  SpecialCharacters,
  SpecialCharactersEssentials,
  FindAndReplace,
  WordCount,
  PasteFromOffice,
} from "ckeditor5";

import "ckeditor5/ckeditor5.css";
import {
  APL_LINK,
  media_uploads_view,
  server_post_data,
} from "../serviceconnection/ServiceConnection";

class MyUploadAdapter {
  constructor(loader) {
    this.loader = loader;
  }
  async upload() {
    const file = await this.loader.file;
    const fd = new FormData();
    fd.append("files", file);

    const res = await server_post_data(media_uploads_view, fd);

    if (res.data?.error) {
      throw new Error(res.data?.message || "Upload failed");
    }

    // NOTE: apne backend response shape ke hisaab se ye line adjust karo
    const uploadedUrl =
      res.data?.data?.[0]?.url || res.data?.files?.[0]?.url || res.data?.url;

    if (!uploadedUrl) {
      throw new Error("Upload response mein URL nahi mila");
    }

    return { default: APL_LINK + uploadedUrl };
  }
  abort() {}
}

function CustomUploadAdapterPlugin(editor) {
  editor.plugins.get("FileRepository").createUploadAdapter = (loader) => {
    return new MyUploadAdapter(loader);
  };
}

// ---- Empty paragraph cleanup: double spacing fix ----
function cleanEditorHtml(html) {
  if (!html) return html;
  return html
    .replace(/<p>(&nbsp;|\s)*<\/p>/gi, "")
    .replace(/(<p>\s*<\/p>\s*){2,}/gi, "");
}

const EDITOR_CONFIG = {
  licenseKey: "GPL",
  plugins: [
    Essentials,
    Paragraph,
    Undo,
    Bold,
    Italic,
    Underline,
    Strikethrough,
    FontSize,
    FontColor,
    FontBackgroundColor,
    Heading,
    BlockQuote,
    Link,
    List,
    ListProperties,
    Alignment,
    Table,
    TableToolbar,
    TableCellProperties,
    TableProperties,
    Image,
    ImageInsertViaUrl,
    ImageUpload,
    ImageInsert,
    ImageResize,
    ImageStyle,
    ImageToolbar,
    MediaEmbed,
    HorizontalLine,
    Indent,
    IndentBlock,
    CodeBlock,
    SpecialCharacters,
    SpecialCharactersEssentials,
    FindAndReplace,
    WordCount,
    PasteFromOffice,
  ],
  toolbar: {
    items: [
      "heading",
      "|",
      "bold",
      "italic",
      "underline",
      "strikethrough",
      "|",
      "fontSize",
      "fontColor",
      "fontBackgroundColor",
      "|",
      "alignment",
      "|",
      "bulletedList",
      "numberedList",
      "outdent",
      "indent",
      "|",
      "link",
      "insertTable",
      "mediaEmbed",
      "|",
      "insertImage",
      "horizontalLine",
      "blockQuote",
      "codeBlock",
      "|",
      "specialCharacters",
      "findAndReplace",
      "|",
      "undo",
      "redo",
    ],
    shouldNotGroupWhenFull: true,
  },
  heading: {
    options: [
      { model: "paragraph", title: "Paragraph", class: "ck-heading_paragraph" },
      {
        model: "heading1",
        view: "h1",
        title: "Heading 1",
        class: "ck-heading_heading1",
      },
      {
        model: "heading2",
        view: "h2",
        title: "Heading 2",
        class: "ck-heading_heading2",
      },
      {
        model: "heading3",
        view: "h3",
        title: "Heading 3",
        class: "ck-heading_heading3",
      },
      {
        model: "heading4",
        view: "h4",
        title: "Heading 4",
        class: "ck-heading_heading4",
      },
    ],
  },
  fontSize: {
    options: [10, 12, 14, "default", 18, 20, 24, 28, 32, 36],
  },
  image: {
    insert: {
      integrations: ["upload", "url"],
    },
    toolbar: [
      "imageStyle:inline",
      "imageStyle:block",
      "imageStyle:side",
      "|",
      "toggleImageCaption",
      "imageTextAlternative",
      "|",
      "resizeImage",
    ],
  },
  table: {
    contentToolbar: [
      "tableColumn",
      "tableRow",
      "mergeTableCells",
      "tableProperties",
      "tableCellProperties",
    ],
  },
  list: {
    properties: {
      styles: true,
      startIndex: true,
      reversed: true,
    },
  },
  link: {
    defaultProtocol: "https://",
    addTargetToExternalLinks: true,
  },
  codeBlock: {
    languages: [
      { language: "plaintext", label: "Plain text" },
      { language: "javascript", label: "JavaScript" },
      { language: "typescript", label: "TypeScript" },
      { language: "python", label: "Python" },
      { language: "css", label: "CSS" },
      { language: "html", label: "HTML" },
      { language: "sql", label: "SQL" },
    ],
  },
};

export default function RichTextEditor({
  value = "",
  onChange,
  placeholder = "Write your content here...",
}) {
  const editorRef = useRef(null);
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (editorRef.current && value && isFirstLoad.current) {
      const currentData = editorRef.current.getData();
      if (currentData !== value) {
        editorRef.current.setData(value);
      }
      isFirstLoad.current = false;
    }
  }, [value]);

  return (
    <CKEditor
      editor={ClassicEditor}
      config={{
        ...EDITOR_CONFIG,
        placeholder,
        extraPlugins: [CustomUploadAdapterPlugin],
      }}
      onReady={(editor) => {
        editorRef.current = editor;
        if (value) {
          editor.setData(value);
          isFirstLoad.current = false;
        }
      }}
      onChange={(_, editor) => {
        if (onChange) onChange(cleanEditorHtml(editor.getData()));
      }}
      onError={(error) => {
        console.error("CKEditor error:", error);
      }}
    />
  );
}
